import { unwrap, type AssignmentIn, type MemberOut, type PermissionGroupOut, type PreviewOut, type RoleOut, type TeamOut } from "@sagot/api-client";
import { PLAN_LIMITS, type PlanKey } from "@sagot/constants";
import { useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useDebounced } from "../lib/slug";
import { queryKeys } from "../lib/query-keys";
import { useApi } from "../session/session-provider";
import { useWorkspaceDetails, useWorkspaceId } from "../session/workspace-context";

const MEMBERS_PAGE_LIMIT = 200;

export function useRoles(enabled = true): UseQueryResult<RoleOut[]> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: queryKeys.roles(wsId), queryFn: () => unwrap(api.GET("/v1/roles")), enabled });
}

/** `GET /v1/permissions`: the catalogue grouped by module (module rows of the matrix). */
export function usePermissionCatalogue(): UseQueryResult<PermissionGroupOut[]> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: queryKeys.permissionCatalogue(wsId), queryFn: () => unwrap(api.GET("/v1/permissions")), staleTime: 10 * 60_000 });
}

export function useMembers(): UseQueryResult<MemberOut[]> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: queryKeys.members(wsId),
    queryFn: async () => (await unwrap(api.GET("/v1/members", { params: { query: { limit: MEMBERS_PAGE_LIMIT } } }))).items,
  });
}

export function useTeams(): UseQueryResult<TeamOut[]> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: queryKeys.teams(wsId), queryFn: () => unwrap(api.GET("/v1/teams")) });
}

/** Invalidates everything a role or assignment change can affect (nav + permissions included). */
export function useInvalidateAccess() {
  const queryClient = useQueryClient();
  const wsId = useWorkspaceId();
  return () => queryClient.invalidateQueries({ queryKey: ["ws", wsId] });
}

interface PreviewBody {
  permissions: string[] | null;
  assignments: AssignmentIn[];
  agent_id: string | null;
  team_id: string | null;
}

export interface PreviewInput {
  permissions?: readonly string[];
  assignments?: readonly AssignmentIn[];
  agentId?: string | null;
  teamId?: string | null;
}

/** `POST /v1/roles/preview-effective` (debounced): the server's view of a draft before saving. */
export function useEffectivePreview(input: PreviewInput, enabled = true): UseQueryResult<PreviewOut> {
  const api = useApi();
  const wsId = useWorkspaceId();
  const serialized = JSON.stringify({
    permissions: input.permissions ? [...input.permissions].sort() : null,
    assignments: input.assignments ? [...input.assignments] : [],
    agent_id: input.agentId ?? null,
    team_id: input.teamId ?? null,
  });
  // Debounce the serialized body (a stable string) so typing in the matrix doesn't spam the API.
  const debounced = useDebounced(serialized);
  return useQuery({
    queryKey: queryKeys.preview(wsId, debounced),
    queryFn: () => unwrap(api.POST("/v1/roles/preview-effective", { body: JSON.parse(debounced) as PreviewBody })),
    enabled,
    placeholderData: (previous) => previous,
    staleTime: 60_000,
  });
}

/** Custom roles are a Pro / Institution feature (Amendment B); the API answers 402 otherwise. */
export function useCustomRolesAllowed(): { allowed: boolean; plan: string | undefined; isPending: boolean } {
  const details = useWorkspaceDetails();
  const plan = details.data?.plan;
  const limits = plan && plan in PLAN_LIMITS ? PLAN_LIMITS[plan as PlanKey] : undefined;
  return { allowed: limits?.customRoles ?? false, plan, isPending: details.isPending };
}
